// Componente Planes para área privada
// Permite a los usuarios autenticados gestionar suscripciones
// Reutiliza componentes existentes del sistema de suscripciones

import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { usePlans } from '../../hooks/usePlans';
import { useSubscription } from '../../hooks/useSubscription';
import SubscriptionFormMP from '../../components/subscription/SubscriptionFormMP';
import SubscriptionCard from '../../components/subscription/SubscriptionCard';
import PlansGrid from '../../components/subscription/PlansGrid';
import NotificationToast from '../../components/subscription/NotificationToast';
import { SUCCESS_MESSAGES } from '../../constants/subscriptions';

/**
 * Componente Planes
 * 
 * Interfaz privada para gestión de suscripciones
 * Muestra planes disponibles y estado actual de la suscripción del usuario
 */
const Planes = () => {
  const { user, isAuthenticated } = useAuth();
  const { plans, loading: plansLoading, error: plansError, refreshPlans } = usePlans();
  const { subscription, loading: subLoading, refreshSubscription } = useSubscription(user?.id);
  
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [notification, setNotification] = useState({ type: '', message: '' });

  /**
   * Muestra una notificación temporal
   */
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  /**
   * Maneja la suscripción a un plan
   */
  const handleSubscribe = (plan) => {
    if (!isAuthenticated) {
      showNotification('error', 'Debes iniciar sesión para suscribirte');
      return;
    }

    setSelectedPlan(plan);
    setShowSubscriptionForm(true);
  };

  /**
   * Maneja el cambio de plan
   */
  const handleChangePlan = (plan) => {
    if (!isAuthenticated) {
      showNotification('error', 'Debes iniciar sesión para cambiar de plan');
      return;
    }

    setSelectedPlan(plan);
    setShowSubscriptionForm(true);
  };

  /**
   * Maneja el éxito en la suscripción/cambio
   */
  const handleSubscriptionSuccess = (result) => {
    showNotification('success', SUCCESS_MESSAGES.SUBSCRIPTION_CREATED);
    setShowSubscriptionForm(false);
    setSelectedPlan(null);
    
    // Refrescar datos
    refreshSubscription();
    refreshPlans();
  };

  /**
   * Maneja errores en la suscripción
   */
  const handleSubscriptionError = (error) => {
    showNotification('error', error);
  };

  /**
   * Maneja la cancelación del formulario
   */
  const handleFormCancel = () => {
    setShowSubscriptionForm(false);
    setSelectedPlan(null);
  };

  /**
   * Maneja el refresco de todos los datos
   */
  const handleRefresh = () => {
    refreshPlans();
    refreshSubscription();
  };

  /**
   * Renderiza el formulario de suscripción
   */
  if (showSubscriptionForm && selectedPlan) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
        <SubscriptionFormMP
          plan={selectedPlan}
          user={user}
          currentSubscription={subscription}
          onSuccess={handleSubscriptionSuccess}
          onError={handleSubscriptionError}
          onCancel={handleFormCancel}
        />
        <NotificationToast
          type={notification.type}
          message={notification.message}
        />
      </div>
    );
  }

  /**
   * Renderiza el contenido principal
   */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Gestión de Planes
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Administra tu suscripción y explora los planes disponibles
        </p>
      </div>

      {/* Estado Actual */}
      <SubscriptionCard
        subscription={subscription}
        isAuthenticated={isAuthenticated}
        loading={subLoading}
        onRefresh={handleRefresh}
      />

      {/* Grid de Planes */}
      <PlansGrid
        plans={plans}
        subscription={subscription}
        loading={plansLoading}
        error={plansError}
        onRefresh={handleRefresh}
        onSubscribe={handleSubscribe}
        onChangePlan={handleChangePlan}
      />

      {/* Notification */}
      <NotificationToast
        type={notification.type}
        message={notification.message}
      />
    </div>
  );
};

export default Planes;
